// Bereich "Kurseinstellungen": Ereignisse rund um das Kursangebot.
//
// Eigener Bestand neben den allgemeinen Ereignissen – hier geht es um
// Änderungen und Aufgaben am Kursangebot. Jeder Eintrag gilt entweder
// für einen einzelnen Mandanten, für ein Kursset (Standardmandanten,
// Sülzle, Weinmann Aach, Carl Spaeter) oder für alle Mandanten.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api.js';
import { courseSetLabel } from '../courseSets.js';
import CourseSettingEventFormModal from './CourseSettingEventFormModal.jsx';
import StatusBadge from './StatusBadge.jsx';
import { formatScope } from './ScopeSelect.jsx';

const STATUSES = ['offen', 'in Arbeit', 'erledigt'];

// Geltungsfilter, Kursset und konkreter Mandant schließen sich aus.
const EXCLUSIVE = ['scope', 'kursset', 'mandant'];

export default function CourseSettings() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Filter als URL-Query-Parameter: bookmarkbar und übersteht Re-Renders.
    const scope = searchParams.get('scope') || '';
    const courseSetFilter = searchParams.get('kursset') || '';
    const mandantFilter = searchParams.get('mandant') || '';

    const [entries, setEntries] = useState([]);
    const [mandanten, setMandanten] = useState([]);
    const [courseSets, setCourseSets] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);

    function setParam(key, value) {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set(key, value);
                else next.delete(key);
                if (value && EXCLUSIVE.includes(key)) {
                    EXCLUSIVE.filter((k) => k !== key).forEach((k) => next.delete(k));
                }
                return next;
            },
            { replace: true },
        );
    }

    async function reload() {
        try {
            setError('');
            const rows = await api.listCourseSettingEvents({
                search,
                status: statusFilter,
                scope,
                course_set: courseSetFilter,
                mandant_id: mandantFilter,
            });
            setEntries(rows);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    // Stammdaten für die Dropdowns einmalig laden.
    useEffect(() => {
        api.listMandanten().then(setMandanten).catch(() => { /* unkritisch */ });
        api.listTotaraCourseSets().then(setCourseSets).catch(() => { /* unkritisch */ });
    }, []);

    // Debounced reload bei Such- oder Filteränderung.
    useEffect(() => {
        const t = setTimeout(reload, 200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, statusFilter, scope, courseSetFilter, mandantFilter]);

    async function handleStatusChange(id, status) {
        try {
            await api.updateCourseSettingEventStatus(id, status);
            setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
        } catch (err) {
            alert('Status-Update fehlgeschlagen: ' + err.message);
        }
    }

    async function handleDelete(id) {
        if (!window.confirm('Diesen Eintrag wirklich löschen?')) return;
        try {
            await api.deleteCourseSettingEvent(id);
            await reload();
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;

    return (
        <div>
            <h1>Kurseinstellungen</h1>
            <p className="muted" style={{ marginTop: -8 }}>
                Ereignisse zum Kursangebot – für einen einzelnen Mandanten, ein
                Kursset oder alle Mandanten.
            </p>

            {error && (
                <div className="panel" style={{ color: 'var(--color-danger)' }}>
                    Fehler: {error}
                </div>
            )}

            <div className="toolbar">
                <input
                    type="text"
                    placeholder="Suche in Titel, Beschreibung, Autor…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 300 }}
                />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">Alle Status</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={scope} onChange={(e) => setParam('scope', e.target.value)}>
                    <option value="">Alle Geltungsbereiche</option>
                    <option value="global">Nur alle Mandanten</option>
                    <option value="kursset">Nur Kursset-bezogen</option>
                    <option value="mandant">Nur mandantenspezifisch</option>
                </select>
                <select
                    value={courseSetFilter}
                    onChange={(e) => setParam('kursset', e.target.value)}
                >
                    <option value="">Alle Kurssets</option>
                    {courseSets.map((cs) => (
                        <option key={cs} value={cs}>{courseSetLabel(cs)}</option>
                    ))}
                </select>
                <select
                    value={mandantFilter}
                    onChange={(e) => setParam('mandant', e.target.value)}
                >
                    <option value="">Alle Mandanten</option>
                    {mandanten.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                </select>
                <div className="spacer" />
                <button className="btn btn--primary" onClick={() => setCreating(true)}>
                    Neuer Eintrag
                </button>
            </div>

            <div className="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Titel</th>
                            <th>Geltung</th>
                            <th>Status</th>
                            <th>Autor</th>
                            <th>Beschreibung</th>
                            <th>Datum</th>
                            <th style={{ width: 1 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="empty">
                                    Keine Einträge gefunden.
                                </td>
                            </tr>
                        ) : entries.map((e) => (
                            <tr
                                key={e.id}
                                className="row-clickable"
                                onClick={() => navigate(`/kurseinstellungen/${e.id}`)}
                            >
                                <td>{e.title}</td>
                                <td>
                                    {e.mandant_id || e.course_set
                                        ? formatScope(e)
                                        : <span className="muted">alle Mandanten</span>}
                                </td>
                                <td>
                                    {/* stopPropagation: Status inline ändern, ohne die
                                        Zeilen-Navigation auszulösen */}
                                    <select
                                        className="status-select"
                                        value={e.status}
                                        onClick={(ev) => ev.stopPropagation()}
                                        onChange={(ev) => handleStatusChange(e.id, ev.target.value)}
                                    >
                                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <div style={{ marginTop: 4 }}><StatusBadge status={e.status} /></div>
                                </td>
                                <td>{e.author || <span className="muted">–</span>}</td>
                                <td style={{ maxWidth: 340, whiteSpace: 'pre-wrap' }}>
                                    {e.description || <span className="muted">–</span>}
                                </td>
                                <td>{formatDate(e.created_at)}</td>
                                <td>
                                    <button
                                        className="btn btn--sm btn--danger"
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            handleDelete(e.id);
                                        }}
                                    >
                                        Löschen
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {creating && (
                <CourseSettingEventFormModal
                    mandanten={mandanten}
                    courseSets={courseSets}
                    onClose={() => setCreating(false)}
                    onSaved={async () => { setCreating(false); await reload(); }}
                />
            )}
        </div>
    );
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE');
}
